import { useEffect, useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from './useAuth'
import { AccountPendingScreen, AuthErrorScreen, LoadingScreen } from './AuthScreens'
import { BrandLogo } from '../brand/BrandLogo'
import { safePostLoginPath } from './authResolution'

function readableLoginError(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = String(error.code)
    if (code.includes('invalid-credential') || code.includes('invalid-login-credentials')) {
      return 'The email or password is incorrect.'
    }
    if (code.includes('too-many-requests')) return 'Too many attempts. Please try again later.'
  }
  return 'Sign-in failed. Please check your details and try again.'
}

export function LoginPage() {
  const { user, role, loading, phase, error, signInWithEmail, clearError } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (user && !loading && phase === 'resolved') {
      if (!role) return
      const from = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from
      const destination = from?.pathname
        ? `${from.pathname}${from.search || ''}${from.hash || ''}`
        : undefined
      navigate(safePostLoginPath(destination, role), { replace: true })
    }
  }, [loading, location.state, navigate, phase, role, user])

  if (user && (loading || phase !== 'resolved')) return <LoadingScreen label="Loading your account..." />
  if (user && error && !loading) return <AuthErrorScreen message={error} />
  if (user && !role && !loading && phase === 'resolved') return <AccountPendingScreen />

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitError(null)
    clearError()
    setSubmitting(true)
    try {
      await signInWithEmail(email.trim(), password)
    } catch (signInError) {
      setSubmitError(readableLoginError(signInError))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="auth-page">
      <section className="portal-card login-card">
        <BrandLogo subtitle="Sanskar Gurukulam" />
        <p className="eyebrow">School fee management</p>
        <h1>Sign in to manage fees</h1>
        <p className="muted">Use the email and temporary password provided by the school office.</p>
        <form className="login-form" onSubmit={handleSubmit}>
          <label htmlFor="email">Email</label>
          <input id="email" name="email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required />
          <label htmlFor="password">Password</label>
          <input id="password" name="password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
          {(submitError || error) && <p className="error-message" role="alert">{submitError || error}</p>}
          <button className="button" type="submit" disabled={submitting}>
            {submitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </section>
    </main>
  )
}
